import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { SITE_LOCALES } from '../nav-link';
import { absoluteUrl } from './site-url';

/**
 * Per-page head tags for the static routes: `/terms` and `/privacy`.
 *
 * Why this exists
 * ---------------
 * Every meta tag on this site used to live in a hand-maintained `index.html`
 * and four generated copies. That is right for the home page and wrong for
 * every other route, because Angular prerenders `/terms` from the same file:
 * the terms page shipped with the home page's `<title>`, description,
 * `og:url`, hreflang set, and, worst of all, its canonical.
 *
 * Google read `/terms`, saw `<link rel="canonical" href="https://ledgerly.download/">`,
 * concluded the page was a duplicate of the home page and dropped it. That is
 * the "Alternate page with proper canonical tag" report: not a warning, a
 * refusal to index. Ten pages were affected, `/terms` and `/privacy` in five
 * languages.
 *
 * The hreflang set was wrong in the same way. On `/terms` it advertised the
 * five home pages as translations of the terms page, which is not reciprocal
 * and invalidates the cluster.
 *
 * `libs/landing/blog/src/lib/blog-seo.service.ts` solves the same problem for
 * posts and is the model for this one. The two are deliberately separate: a
 * post's head is driven by front matter and carries JSON-LD and article
 * timestamps, while these pages are a fixed set with a fixed shape.
 *
 * Everything here is synchronous, so it lands in the prerendered HTML, which
 * is what a crawler and a reader without JavaScript both get.
 */

const NAMED = ['description', 'twitter:title', 'twitter:description'];
const PROPERTIES = ['og:url', 'og:title', 'og:description'];

interface Snapshot {
  title: string;
  named: Map<string, string | null>;
  properties: Map<string, string | null>;
  canonical: string | null;
  /** The static hreflang links, detached while this page's own set is up. */
  alternates: Element[];
}

export interface StaticPageSeo {
  /** The route without a locale prefix, e.g. `/terms`. */
  route: string;
  title: string;
  description: string;
}

@Injectable({ providedIn: 'root' })
export class StaticPageSeoService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);

  private snapshot: Snapshot | null = null;
  private generation = 0;

  /** Returns the generation to hand back to `restore` on destroy. */
  apply(page: StaticPageSeo): number {
    const lang = this.document.documentElement.lang || 'en';
    const url = absoluteUrl(lang, page.route);

    this.take();

    this.title.setTitle(page.title);
    this.setNamed('description', page.description);
    this.setNamed('twitter:title', page.title);
    this.setNamed('twitter:description', page.description);
    this.setProperty('og:url', url);
    this.setProperty('og:title', page.title);
    this.setProperty('og:description', page.description);
    this.setCanonical(url);
    this.setAlternates(page.route);

    return ++this.generation;
  }

  /**
   * Puts the head back the way the static index.html had it.
   *
   * A stale generation is ignored, which keeps a page-to-page navigation safe:
   * the router builds the next component before destroying the last one, so
   * the restore can arrive after the next apply.
   */
  restore(generation: number): void {
    if (!this.snapshot || generation !== this.generation) return;

    const snapshot = this.snapshot;
    this.title.setTitle(snapshot.title);

    for (const [name, content] of snapshot.named) {
      if (content === null) this.meta.removeTag(`name="${name}"`);
      else this.setNamed(name, content);
    }
    for (const [property, content] of snapshot.properties) {
      if (content === null) this.meta.removeTag(`property="${property}"`);
      else this.setProperty(property, content);
    }

    if (snapshot.canonical === null) this.canonicalLink()?.remove();
    else this.setCanonical(snapshot.canonical);

    this.clearOwned();
    for (const link of snapshot.alternates) this.document.head.appendChild(link);

    this.snapshot = null;
    this.generation++;
  }

  // --- snapshot -----------------------------------------------------------

  /** Only the first apply snapshots: a later one would record page state. */
  private take(): void {
    if (this.snapshot) {
      this.clearOwned();
      return;
    }

    const alternates = Array.from(
      this.document.head.querySelectorAll('link[rel="alternate"][hreflang]')
    );
    for (const link of alternates) link.remove();

    this.snapshot = {
      title: this.title.getTitle(),
      named: new Map(
        NAMED.map((n) => [n, this.meta.getTag(`name="${n}"`)?.content ?? null])
      ),
      properties: new Map(
        PROPERTIES.map((p) => [
          p,
          this.meta.getTag(`property="${p}"`)?.content ?? null,
        ])
      ),
      canonical: this.canonicalLink()?.getAttribute('href') ?? null,
      alternates,
    };
  }

  // --- head plumbing ------------------------------------------------------

  private setNamed(name: string, content: string): void {
    this.meta.updateTag({ name, content }, `name="${name}"`);
  }

  private setProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content }, `property="${property}"`);
  }

  private canonicalLink(): HTMLLinkElement | null {
    return this.document.head.querySelector('link[rel="canonical"]');
  }

  private setCanonical(href: string): void {
    let link = this.canonicalLink();
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private owned(tag: string): HTMLElement {
    const element = this.document.createElement(tag);
    element.setAttribute('data-page-seo', '');
    this.document.head.appendChild(element);
    return element;
  }

  private clearOwned(): void {
    for (const element of Array.from(
      this.document.head.querySelectorAll('[data-page-seo]')
    )) {
      element.remove();
    }
  }

  /**
   * Every locale has every static route, so the cluster is the full set and
   * each entry points at the same route in its own language.
   */
  private setAlternates(route: string): void {
    for (const locale of SITE_LOCALES) {
      const link = this.owned('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', locale.code);
      link.setAttribute('href', absoluteUrl(locale.code, route));
    }

    const fallback = this.owned('link');
    fallback.setAttribute('rel', 'alternate');
    fallback.setAttribute('hreflang', 'x-default');
    fallback.setAttribute('href', absoluteUrl('en', route));
  }
}
